import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useTireSize } from "../blocks/Tires/Results/Filters/TireSizeFilters/useTireSize";
import { useWheelBrands } from "../blocks/Wheels/Filters/WheelBrands/useWheelBrands";
import { useWheelPrice } from "../blocks/Wheels/Filters/WheelPrice/useWheelPrice";
import { dealerWheelPriceFilters } from "../blocks/Wheels/Filters/WheelPrice/useWheelPriceOptions";
import { PartTypeWheel } from "../constants/partTypes";
import { productTypeCombo, productTypeWheel } from "../constants/productTypes";
import { LanguageMap } from "../i18n/useLanguage";
import { DealerVehicleResultsRoute } from "../navigation/DealerRoutes";
import { useRouteParams } from "../navigation/useRouteParams";
import { matchPartPricing } from "../util/matchPartPricing";
import { useGetVehicleInfo } from "./useGetVehicleInfo";
import { PartPricingItem } from "./useSearchTiresByVehicle";

const productTypes = new Set<string | undefined>([
  productTypeWheel,
  productTypeCombo,
]);

export const useSearchWheelsByVehicle = (
  swrOptions: SWRConfiguration<FetcherResult<Payload>> = {}
): ModifiedSWRResponse<
  FetcherResult<Payload>,
  SearchWheelsByVehiclePayload
> => {
  const { data: vehicleInfo } = useGetVehicleInfo();
  const route = useRouteParams(DealerVehicleResultsRoute);
  const shouldFetchWheels =
    productTypes.has(route?.productType) && Boolean(vehicleInfo?.vehicleId);
  const [tireSize] = useTireSize();
  const [brands] = useWheelBrands();
  const [pricing] = useWheelPrice();

  const tireSizeInfo = vehicleInfo?.vehicleFullTireSizeInfo.find(
    ({ vehicleSubmodelTiresizeId }) => vehicleSubmodelTiresizeId === tireSize
  );

  const { data, ...result } = useSwrGet<Payload>(
    shouldFetchWheels
      ? "/retailSiteWebService/parts/wheel/searchWheelsByVehicle.php"
      : null,
    {
      dealerId: route?.dealerId || "",
      isNational: "0",
      vehicleId: String(vehicleInfo?.vehicleId || ""),
    },
    swrOptions
  );

  if (!data) {
    return result as ModifiedSWRResponse<
      FetcherResult<Payload>,
      SearchWheelsByVehiclePayload
    >;
  }

  const filteredParts = data.filter(
    ({ partDetails, partPricing, partSummary }) => {
      const rimFilter = tireSizeInfo
        ? tireSizeInfo.rimDiameter ===
          partDetails.primaryPart.wheelDiameter + ".00"
        : true;
      const brandsFilter =
        brands.length > 0
          ? brands.includes(partDetails.primaryPart.wheelManufacturerName)
          : true;
      const priceFilter =
        pricing.length > 0
          ? pricing
              .filter((index) => index in dealerWheelPriceFilters)
              .some((index) => {
                const range = dealerWheelPriceFilters[index];
                return matchPartPricing(partPricing, range);
              })
          : true;
      return (
        rimFilter &&
        brandsFilter &&
        priceFilter &&
        !partSummary.primaryPart.hideItem
      );
    }
  );

  return { ...result, data: { allParts: data, filteredParts } };
};

type Payload = WheelPartItem[];

export type SearchWheelsByVehiclePayload = {
  allParts: Payload;
  filteredParts: WheelPartItem[];
};

export type WheelPartItem = {
  partDetails: {
    primaryPart: PrimaryWeelPart;
    relatedParts: WheelPartSummary[];
    secondaryParts: PrimaryWeelPart[];
  };
  partPricing: {
    primaryPart: WheelPartPricing;
    relatedParts: WheelPartPricing[];
    secondaryParts: WheelPartPricing[];
  };
  partStock: {
    primaryPart: WheelStockItem[];
    relatedParts: WheelStockItem[];
    secondaryParts: WheelStockItem[];
  };
  partSummary: {
    primaryPart: WheelPartSummary;
    relatedParts: WheelPartSummary[];
    secondaryParts: WheelPartSummary[];
  };
  partTire?: never;
  partVisualizerAsset: string[];
  partWheel?: never;
};

type WheelStockItem = {
  itemId: string;
  itemType: string;
  minutesSinceLastStockUpdate: number;
  optionId: string;
  sortOrder: string;
  stockApiForceRefreshOnLoad: boolean;
  stockApiQueryUrl: string;
  stockLocationId: string;
  stockLocationKey: string;
  stockLocationName: string;
  stockLocationType: string;
  stockLocationWebsite: string;
  stockQty: string;
  stockQtyLocal: string;
  stockQtyLocalPlus: string;
  stockQtyNationWide: string;
  tireLinkEnabled: boolean;
  tireStockApiQueryEngineCode: string;
};

export type WheelPartSummary = {
  hideItem: boolean;
  itemDescription: LanguageMap;
  itemHasDealerPricing: boolean;
  itemHasStock: boolean;
  itemId: string;
  itemImageLink: string;
  itemNationalPartNumber: string;
  itemOriginalEquipmentFlag: string;
  itemPartNumber: string;
  itemPartOption: null;
  itemShippingMetrics: null;
  itemTitle: LanguageMap;
  itemType: PartTypeWheel;
  supportConsumerOrder: boolean;
  supportDealerOrder: boolean;
};

type PrimaryWeelPart = {
  itemId: string;
  itemType: PartTypeWheel;
  wheelAxlePartner: string;
  wheelAxleType: string;
  wheelColour: string;
  wheelComment: string;
  wheelDescription: LanguageMap;
  wheelDiameter: string;
  wheelDiscontinued: string;
  wheelFinish: string;
  wheelFinishName: LanguageMap;
  wheelImageLink: string;
  wheelManufacturerId: string;
  wheelManufacturerName: string;
  wheelName: LanguageMap;
  wheelNationalPartNumber: string;
  wheelOffset: string;
  wheelPartNumber: string;
  wheelSeasonalSizeCode: string;
  wheelVehicleImages: [];
  wheelWidth: string;
};

export type WheelPartPricing = {
  itemId: string;
  itemType: PartTypeWheel;
  link: unknown[];
  price: {
    [key: string]: PartPricingItem;
  };
  priceLastUpdated: string;
  prioritizeInSearch: boolean;
};

export type ResultParameters = {
  dealerId: number;
  originalEquipmentFilter: string;
  season: number;
  vehicleId: number;
  vehicleSubmodelTireSizeId: number;
};
